<template>
  <Loader :value="promises.fetchOne" class="task-views-mutate">
    <Card
      tag="form"
      @submit.prevent="submit"
      v-bind="$attrs"
      :title="$t(id ? 'editTask' : 'createTask')"
      class="task-views-mutate__card"
      :class="['task-views-mutate__card--' + this.view]"
    >
      <template #header>
        <component
          v-if="data.reference"
          :is="ReferenceComponents[data.reference.onModel]"
          :value="data.reference.ref"
        />
      </template>
      <template #default>
        <InputComponent
          :label="labels.title"
          v-model="data.title"
          required
          style="--grid-area: title"
          :is-valid="validator.fields.title.valid"
          :validation-message="validator.fields.title.error"
        />
        <InputComponent
          :label="labels.description"
          :is-valid="validator.fields.description.valid"
          :validation-message="validator.fields.description.error"
          style="--grid-area: description"
        >
          <RichTextComponent v-model="data.description" />
        </InputComponent>
        <InputComponent :value="data.dueAt" :label="labels.dueAt" style="--grid-area: dueAt">
          <InputDate v-model="data.dueAt" expanded />
        </InputComponent>

        <InputComponent
          :label="labels.members"
          required
          :is-valid="validator.fields.members.valid"
          :validation-message="validator.fields.members.error"
          style="--grid-area: members"
        >
          <UserSelect
            v-model="data.members"
            multiple
            search
            :items="fetchUsers"
            :title="$t('selectx', { x: $tc('member', 2).toLowerCase() })"
            class="elder-input__element"
          />
        </InputComponent>

        <Tags
          data-field="tags"
          :label="$tc('tag', 2)"
          style="--grid-area: tags"
          v-model="data.tags"
          :placeholder="$tc('add')"
          autocomplete="/api/task/tags"
        />

        <FileComponent v-model="data.attachments" :label="labels.attachments" multiple style="--grid-area: attachments">
          <template #drop-message="{ extensions }">
            <FileOnDrop :extensions="extensions" />
          </template>
        </FileComponent>
        <MediaComponent
          v-model="data.images"
          :label="labels.images"
          :upload-options="{ compression: 'default' }"
          size="contain"
          placement="inside"
          multiple
          style="--grid-area: images"
          :description="false"
        >
          <template #drop-message>
            <MediaOnDrop />
          </template>
        </MediaComponent>
      </template>
      <template #footer>
        <slot name="footer" />
        <ButtonComponent
          type="submit"
          :label="$t(id ? 'update' : 'create')"
          theme="primary"
          :promise="promises.mutate"
          :disabled="!validator.passes"
          @onSuccess="onUpdate"
          :icon="['fal', id ? 'arrow-to-right' : 'plus']"
        />
        <DropdownComponent v-if="id" :close-on-click="false" style="margin-left: auto">
          <ButtonComponent :icon="{ icon: ['fal', 'ellipsis-v'], size: 'lg' }" />
          <template #dropdown>
            <ButtonComponent
              v-if="id"
              :label="$t('delete')"
              confirm
              @click="() => remove({ id })"
              :promise="promises.remove"
              @onSuccess="onRemove"
              :icon="['fal', 'tasks']"
            />
          </template>
        </DropdownComponent>
      </template>
    </Card>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import Model from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import UserApi from '@/api/User'

import { InputComponent } from 'vue-elder-input'
import { MediaComponent } from '@kvass/media'
import { FileComponent } from '@kvass/file'
import { DropdownComponent } from 'vue-elder-dropdown'
import RichTextComponent from '@kvass/rich-text'
import Card from '@/components/Card'
import UserSelect from '@/components/User/Select/Modal'
import MediaOnDrop from '@/components/vendors/kvass-media/onDrop'
import FileOnDrop from '@/components/vendors/kvass-file/onDrop'
import ReferenceComponents from '@/components/Task/Reference'
import InputDate from '@/components/InputDate'
import Tags from '@/components/Tags'

export default {
  props: {
    id: String,
    defaultData: Object,
  },
  watch: {
    id: {
      handler(id) {
        if (!id) return this.set({})
        return this.fetch({ id })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Task', ['item', 'promises', 'states']),
    ...mapi18n('labels', {
      title: 'title',
      members: ['member', 2],
      images: ['image', 2],
      dueAt: 'deadline',
      attachments: ['attachment', 2],
      description: 'description',
    }),
    view() {
      if (!this.$path('data.reference.onModel')) return 'default'

      switch (this.data.reference.onModel) {
        default:
          return 'default'
      }
    },
    itemMerged() {
      return {
        ...this.defaultData,
        ...this.item,
      }
    },
  },
  data() {
    return {
      ReferenceComponents,
    }
  },
  methods: {
    ...mapMutations('Task', ['set']),
    ...mapActions('Task', ['fetch', 'mutate', 'remove']),
    submit() {
      return this.mutate([
        {
          ...this.getData({ diff: true }),
          id: this.id,
          reference: this.id ? undefined : this.defaultData.reference,
        },
        {
          strategy: (stateItem, { id }) => {
            return { ...this.getData({ transform: false }), id }
          },
        },
      ])
    },
    fetchUsers(filter) {
      return UserApi.get({ filter: { ...filter, type: 'user' } }, { query: 'UserSearch' })
    },
    onUpdate() {
      this.$emit('onUpdate', this.data)
      this.$bus.$emit('task:update', this.data)
    },
    onRemove() {
      this.$emit('onRemove')
      this.$bus.$emit('task:remove', this.id)
    },
  },
  created() {
    if (!this.data.members.length)
      this.data.members.push({
        id: this.$store.state.user.id,
        name: this.$store.state.user.name,
        email: this.$store.state.user.email,
        avatar: this.$store.state.user.avatar,
      })
  },
  mixins: [
    Validator({
      rules: {
        title: 'required',
        members: 'array|min:1',
        images: 'array',
        dueAt: 'date',
        attachments: 'array',
        description: 'string',
      },
      labels: 'labels',
      data: 'data',
    }),
    Model({
      source: 'itemMerged',
      model: {
        id: null,
        status: 'undone',
        title: null,
        description: null,
        dueAt: null,
        members: [],
        images: [],
        attachments: [],
        reference: null,
        tags: [],
      },
      transform: {
        members(v) {
          return v && v.map(e => e.id)
        },
      },
      guard: true,
    }),
  ],
  components: {
    Card,
    InputComponent,
    UserSelect,
    RichTextComponent,
    MediaOnDrop,
    FileComponent,
    FileOnDrop,
    InputDate,
    DropdownComponent,
    MediaComponent,
    Tags,
  },
}
</script>

<style lang="scss">
.task-views-mutate {
  max-width: 1000px;

  &__card {
    & > .card__header {
      min-height: 150px;
    }

    & > .card__content {
      display: grid;
      gap: 1rem 2rem;
      grid-template-columns: repeat(2, 1fr);
      grid-template-areas:
        'card-title card-title'
        'title members'
        'dueAt tags'
        'description description'
        'images attachments';

      & > * {
        grid-area: var(--grid-area);
      }
    }

    &-actions {
      margin-left: auto;
      margin-bottom: auto;
      font-size: 0.8em;
    }
  }
  .kvass-media-droparea {
    min-width: 250px;
    height: 200px;
  }
}
</style>
